import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';

// Flag via flagcdn.com — funziona su tutti i sistemi incluso Windows
const Flag = ({ code, className = "w-5 h-3.5 rounded-sm object-cover" }) => (
  <img
    src={`https://flagcdn.com/w40/${code}.png`}
    alt={code}
    className={className}
  />
);

const LANGUAGES = [
  { code: 'it',    label: 'Italiano',   flag: 'it' },
  { code: 'en',    label: 'English',    flag: 'gb' },
  { code: 'fr',    label: 'Français',   flag: 'fr' },
  { code: 'de',    label: 'Deutsch',    flag: 'de' },
  { code: 'es',    label: 'Español',    flag: 'es' },
  { code: 'pt',    label: 'Português',  flag: 'pt' },
  { code: 'ar',    label: 'العربية',    flag: 'sa' },
  { code: 'zh-CN', label: '中文',       flag: 'cn' },
  { code: 'ru',    label: 'Русский',    flag: 'ru' },
  { code: 'ja',    label: '日本語',     flag: 'jp' },
  { code: 'ko',    label: '한국어',     flag: 'kr' },
  { code: 'nl',    label: 'Nederlands', flag: 'nl' },
  { code: 'pl',    label: 'Polski',     flag: 'pl' },
  { code: 'tr',    label: 'Türkçe',     flag: 'tr' },
  { code: 'uk',    label: 'Українська', flag: 'ua' },
];

// Legge la lingua attiva dal cookie googtrans.
// Il widget di Google può scrivere sia /it/en che solo /en — gestiamo entrambi.
const readLangFromCookie = () => {
  const m = document.cookie.match(/googtrans=(?:\/[a-zA-Z-]+)?\/([a-zA-Z-]+)/);
  return m ? m[1] : null;
};

// Cancella googtrans su tutte le varianti di dominio/path che il browser
// o il widget potrebbero aver usato per scriverlo.
const clearGoogTransCookies = () => {
  const hostname = window.location.hostname;
  const past = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
  const variants = [
    `googtrans=; ${past}; path=/`,
    `googtrans=; ${past}; path=/; domain=${hostname}`,
    `googtrans=; ${past}; path=/; domain=.${hostname}`,
  ];
  // Se il sito è su un sottodominio (es. admin1.noleggioincloud.app),
  // cancella anche sul dominio root.
  const parts = hostname.split('.');
  if (parts.length > 2) {
    const root = parts.slice(-2).join('.');
    variants.push(`googtrans=; ${past}; path=/; domain=.${root}`);
  }
  variants.forEach(v => { document.cookie = v; });
};

// Pulisce tutto ciò che Google Translate lascia nel browser tra una
// sessione e l'altra: cookie, storage, elementi DOM iniettati.
const nukeGoogleTranslate = () => {
  clearGoogTransCookies();

  // localStorage / sessionStorage
  try {
    ['localStorage', 'sessionStorage'].forEach(store => {
      const s = window[store];
      Object.keys(s)
        .filter(k => /googtrans|^gt_|google_translate/i.test(k))
        .forEach(k => s.removeItem(k));
    });
  } catch (_) {}

  // Elementi DOM iniettati dal widget
  try {
    [
      '#goog-gt-tt',
      '.goog-te-banner-frame',
      '.goog-te-ftab-frame',
      '#google_translate_element2',
      '.skiptranslate',
    ].forEach(sel => {
      document.querySelectorAll(sel).forEach(el => el.remove());
    });
    // Il widget aggiunge class="translated-ltr" (o -rtl) sull'<html>
    document.documentElement.className = document.documentElement.className
      .replace(/\btranslated-\w+\b/g, '')
      .trim();
    // E a volte uno style top:40px sul <body> per il banner
    document.body.style.top = '';
  } catch (_) {}
};

const LanguageToggle = ({ className = '' }) => {
  const [current, setCurrent] = useState(LANGUAGES[0]);
  const [open, setOpen]       = useState(false);
  const ref                   = useRef(null);

  useEffect(() => {
    // Rimuove il parametro temporaneo ?_gt=... usato da switchLanguage per
    // forzare un reload pulito, in modo che non rimanga nell'URL visibile.
    const url = new URL(window.location.href);
    if (url.searchParams.has('_gt')) {
      url.searchParams.delete('_gt');
      window.history.replaceState(null, '', url.toString());
    }

    // Legge la lingua corrente dal cookie al mount
    const code = readLangFromCookie();
    if (code && code !== 'it') {
      const found = LANGUAGES.find(l => l.code === code);
      if (found) setCurrent(found);
    }

    // Chiude il dropdown cliccando fuori
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchLanguage = (lang) => {
    setOpen(false);

    // Stessa lingua già attiva → non fare niente
    if (lang.code === current.code) return;

    // 1. Pulisce tutto lo stato precedente di Google Translate
    nukeGoogleTranslate();

    // 2. Se la destinazione non è l'italiano, imposta il nuovo cookie
    if (lang.code !== 'it') {
      document.cookie = `googtrans=/it/${lang.code}; path=/`;
      // Scrive anche sul dominio root per i sottodomini custom
      const parts = window.location.hostname.split('.');
      if (parts.length > 2) {
        const root = parts.slice(-2).join('.');
        document.cookie = `googtrans=/it/${lang.code}; path=/; domain=.${root}`;
      }
    }

    setCurrent(lang);

    // 3. Forza un reload PULITO aggiungendo un timestamp come parametro
    // temporaneo: questo impedisce al browser di servire la pagina dalla
    // cache in-memory (che è quello che succede con location.reload() o
    // con location.href = location.href quando l'URL è identico).
    // Il parametro _gt viene rimosso subito dopo dal server/SPA routing
    // e non impatta la navigazione interna dell'app.
    const url = new URL(window.location.href);
    url.searchParams.set('_gt', Date.now());
    window.location.replace(url.toString());
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition text-sm font-medium"
      >
        <Flag code={current.flag} />
        <span className="hidden sm:inline">{current.code === 'zh-CN' ? 'ZH' : current.code.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          <div className="py-1 max-h-80 overflow-y-auto">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => switchLanguage(lang)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-blue-50 transition ${
                  current.code === lang.code ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
                }`}
              >
                <Flag code={lang.flag} className="w-6 h-4 rounded-sm object-cover flex-shrink-0" />
                <span>{lang.label}</span>
                {current.code === lang.code && <Check className="ml-auto w-3.5 h-3.5 text-blue-500" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageToggle;
